import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "services/api.service";
import { Pickup } from "types/pickup";
import { useAlert } from "./useAlert";

export default function useLogisticsPickups({ search: argSearch }: { search?: string } = {}) {
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const { search = argSearch } = useLocation();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const { alertError, alertSuccess } = useAlert();

  const getLogisticsPickups = useCallback(() => {
    const searchHash = new URLSearchParams(search);
    if (!searchHash.get("page[size]")) {
      searchHash.append("page[size]", String(25));
    }
    setLoading(true);
    api
      .get(`/logistics_pickups?${searchHash.toString()}`)
      .then(
        ({
          data: {
            pickups: fetchedPickups,
            meta: { totalPages: fetchedTotalPages, count: fetchedCount },
          },
        }) => {
          setPickups(fetchedPickups);
          setTotalPages(fetchedTotalPages);
          setCount(fetchedCount);
        },
      )
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search]);

  const confirmPickup = (pickupUuid: string) => {
    return api
      .put(`/logistics_pickups/${pickupUuid}/confirm_pickup_date`)
      .then(() => {
        alertSuccess("Pickup scheduled!");
        getLogisticsPickups();
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const cancelPickup = (pickupUuid: string, cancellationReason: string) => {
    return api
      .post(`/logistics_pickups/${pickupUuid}/cancel`, { cancellationReason })
      .then(() => {
        alertSuccess(`Successfully canceled the pickup`);
        getLogisticsPickups();
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const reschedulePickup = (
    pickupUuid: string,
    rescheduleReason: string,
    scheduledAt?: string,
    confirmedTime?: string,
  ) => {
    return api
      .post(`/logistics_pickups/${pickupUuid}/reschedule`, {
        rescheduled_reason: rescheduleReason,
        scheduled_at: scheduledAt,
        confirmed_time: confirmedTime,
      })
      .then(() => {
        alertSuccess("Pickup rescheduled!");
        getLogisticsPickups();
      });
  };

  useEffect(() => getLogisticsPickups(), [getLogisticsPickups, search]);

  return {
    pickups,
    totalPages,
    count,
    error,
    loading,
    getLogisticsPickups,
    confirmPickup,
    cancelPickup,
    reschedulePickup,
  };
}
